import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment'; // Asegúrate de que esta ruta sea correcta

@Injectable({
    providedIn: 'root'
})
export class TripsService {
    // All headers (Authorization + ngrok-skip-browser-warning) are handled by apiInterceptor

    constructor(private http: HttpClient) { }

    getMyTrips(): Observable<any[]> {
        return this.http.get<any[]>(`${environment.apiUrl}/trips/my-trips`);
    }

    getMyProfile(): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/drivers/me`);
    }

    getMyLines(): Observable<any[]> {
        return this.http.get<any[]>(`${environment.apiUrl}/lines`);
    }

    getLineWithRoutes(lineId: number): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/lines/${lineId}`);
    }

    selfDispatch(dto: { lineId: number; routeId: number; vehicleId: number }): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/trips/self-dispatch`, dto);
    }

    getMyTripHistory(filters?: { vehicleId?: number; startDate?: string; endDate?: string }): Observable<any[]> {
        let params = '';
        if (filters?.vehicleId) params += `vehicleId=${filters.vehicleId}&`;
        if (filters?.startDate) params += `startDate=${filters.startDate}&`;
        if (filters?.endDate) params += `endDate=${filters.endDate}&`;
        const query = params ? `?${params.slice(0, -1)}` : '';
        return this.http.get<any[]>(`${environment.apiUrl}/trips/my-history${query}`);
    }

    startTrip(id: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/trips/${id}/start`, {});
    }

    getTripDetails(id: number): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/trips/${id}`);
    }

    markCheckpointReached(tripId: number, checkpointId: number): Observable<any> {
        return this.http.patch<any>(`${environment.apiUrl}/trips/${tripId}/checkpoints/${checkpointId}`, {});
    }

    postCheckpointReach(tripId: number, checkpointId: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/trips/${tripId}/checkpoint/${checkpointId}/reach`, {});
    }

    postArrival(id: number, arrivalAt: Date): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/trips/${id}/arrival`, { arrivalAt });
    }

    postManualFinish(id: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/trips/${id}/manual-finish`, {});
    }

    finishTrip(
        id: number,
        passengerCount?: number,
        arrivalTime?: Date,
        reachedCheckpoints?: { id: number; reachedAt: string }[],
    ): Observable<any> {
        // Send the per-checkpoint visit times so the server can reconcile any that failed to
        // sync mid-trip without collapsing them all onto the finish timestamp.
        return this.http.post<any>(`${environment.apiUrl}/trips/${id}/finish`, {
            passengerCount,
            arrivalTime,
            reachedCheckpoints,
        });
    }

    postLocation(vehicleId: number, lat: number, lng: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/location`, { vehicleId, lat, lng });
    }

    getTripBoardings(tripId: number): Observable<{ total: number; boardings: any[] }> {
        return this.http.get<any>(`${environment.apiUrl}/boardings/trip/${tripId}`);
    }

    scanPassenger(
        tripId: number,
        passengerQrToken: string,
        lat?: number,
        lng?: number,
    ): Observable<{ message: string; boardingId: number; passengerName: string }> {
        const body: any = { passengerQrToken };
        if (lat != null && lng != null) { body.lat = lat; body.lng = lng; }
        return this.http.post<any>(`${environment.apiUrl}/boardings/trip/${tripId}/scan-passenger`, body);
    }

    scanPassengerAndPay(
        tripId: number,
        passengerQrToken: string,
        amount: number,
        lat?: number,
        lng?: number,
    ): Observable<{ message: string; boardingId: number; passengerName: string; paid: boolean }> {
        const body: any = { passengerQrToken, amount };
        if (lat != null && lng != null) { body.lat = lat; body.lng = lng; }
        return this.http.post<any>(`${environment.apiUrl}/boardings/trip/${tripId}/scan-and-pay`, body);
    }

    checkoutPassenger(
        tripId: number,
        passengerQrToken: string,
        lat?: number,
        lng?: number,
    ): Observable<{ message: string; passengerName: string; checkedOutAt: string }> {
        const body: any = { passengerQrToken };
        if (lat != null && lng != null) { body.lat = lat; body.lng = lng; }
        return this.http.post<any>(`${environment.apiUrl}/boardings/trip/${tripId}/checkout-passenger`, body);
    }


    getBankAccounts(): Observable<any[]> {
        return this.http.get<any[]>(`${environment.apiUrl}/drivers/me/bank-accounts`);
    }

    addBankAccount(accountNumber: string, bankName: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/drivers/me/bank-accounts`, { accountNumber, bankName });
    }

    setDefaultBankAccount(id: number): Observable<void> {
        return this.http.patch<void>(`${environment.apiUrl}/drivers/me/bank-accounts/${id}/default`, {});
    }

    deleteBankAccount(id: number): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/drivers/me/bank-accounts/${id}`);
    }

    requestAccountDeletion(): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/auth/request-account-deletion`, {});
    }

    deleteAccount(otpCode: string): Observable<any> {
        return this.http.delete<any>(`${environment.apiUrl}/auth/account`, { body: { otpCode } });
    }
}